import { z, ZodRawShape } from 'zod';

const buildHookSchema = <T extends ZodRawShape>(hookType: T) => {
    return z.object({
        isActive: z.boolean(),
        ...hookType,
    });
};
const buildLicenseHookSchema = <T extends ZodRawShape>(hookType: T) => {
    return buildHookSchema({ ...hookType, hasLicense: z.boolean() });
};

/**
 * Hook types
 */

// Drive
const driveSchema = z.enum(['drive_microsoft_graph', 'drive_dropbox', 'drive_google']);
export type Drive = z.infer<typeof driveSchema>;
const driveHookSchema = buildHookSchema({
    drives: z.record(driveSchema, z.boolean()),
});
export type DriveHook = z.infer<typeof driveHookSchema>;

// Calendar
const calendarHookSchema = buildHookSchema({
    label: z.string(),
    defaultView: z.enum(['calendar', 'list']),
});
export type CalendarHook = z.infer<typeof calendarHookSchema>;

// FileBank
const fileBankHookSchema = buildHookSchema({
    orderBy: z.string(),
    order: z.enum(['ASC', 'DESC']),
});
export type FileBankHook = z.infer<typeof fileBankHookSchema>;

// Messenger
const messengerHookSchema = buildHookSchema({
    maxParticipants: z.number(),
    minParticipantsForSpotDiscussion: z.number(),
});
export type MessengerHook = z.infer<typeof messengerHookSchema>;

// OnlyOffice
export const onlyOfficeTemplateSchema = z.object({
    label: z.string(),
    description: z.string(),
    extension: z.string(),
    icon: z.string(),
    color: z.string(),
});
const onlyOfficeHookSchema = buildLicenseHookSchema({
    label: z.string(),
    description: z.string(),
    color: z.string(),
    templates: onlyOfficeTemplateSchema.array(),
});
export type OnlyOfficeHook = z.infer<typeof onlyOfficeHookSchema>;

// Studio
const studioHookSchema = buildLicenseHookSchema({
    oldViewOptionProperty: z.boolean(),
});
export type StudioHook = z.infer<typeof studioHookSchema>;

// AdminAdvanced
const adminAdvancedHookSchema = buildHookSchema({
    sendAlert: z.boolean(),
});
export type AdminAdvancedHook = z.infer<typeof adminAdvancedHookSchema>;

// SpotRssFeed
const spotRssFeedSchema = buildHookSchema({});
export type SpotRssFeed = z.infer<typeof spotRssFeedSchema>;

// SharedContent
const sharedContentHookSchema = buildHookSchema({});
export type SharedContentHook = z.infer<typeof sharedContentHookSchema>;

// Widget
const widgetHookSchema = buildHookSchema({
    create: z.boolean(),
    autorize: z.array(z.enum(['message', 'comment', 'board', 'article']).optional()),
});
export type WidgetHook = z.infer<typeof widgetHookSchema>;

// MagicPad
const magicPadHookSchema = buildLicenseHookSchema({
    label: z.string(),
});
export type MagicPadHook = z.infer<typeof magicPadHookSchema>;

// MediaLibraryHook
const mediaLibraryHookSchema = buildLicenseHookSchema({});
export type MediaLibraryHook = z.infer<typeof mediaLibraryHookSchema>;

// ExtraBot
const extraBotHookSchema = buildHookSchema({
    label: z.string(),
    create: z.boolean(),
});
export type ExtraBotHook = z.infer<typeof extraBotHookSchema>;

// TwoFactorAuthentication
const twoFactorAuthenticationProviderSchemaKey = z.enum(['loginPassword', 'saml', 'oAuth']);
const twoFactorAuthenticationProviderSchemaValue = z.enum(['no', 'optional', 'mandatory']);
const twoFactorAuthenticationProviderSchema = z.record(
    twoFactorAuthenticationProviderSchemaKey,
    twoFactorAuthenticationProviderSchemaValue,
);
const twoFactorAuthenticationHookSchema = buildLicenseHookSchema({
    name: z.string(),
    providers: twoFactorAuthenticationProviderSchema,
    banLifespan: z.number(),
});
export type TwoFactorAuthenticationHook = z.infer<typeof twoFactorAuthenticationHookSchema>;

// ExternalApp
const externalAppHookSchema = buildLicenseHookSchema({});
export type ExternalAppHookSchema = z.infer<typeof externalAppHookSchema>;

// DigitalWorkplace
const digitalWorkplaceHookSchema = buildLicenseHookSchema({
    v2: z.boolean(),
});
export type DigitalWorkplaceHookSchema = z.infer<typeof digitalWorkplaceHookSchema>;

// Office
const officeHookSchema = buildLicenseHookSchema({
    label: z.string(),
});
export type OfficeHook = z.infer<typeof officeHookSchema>;

const luccaAbsenceHookSchema = buildLicenseHookSchema({ label: z.string() });
export type LuccaAbsenceHook = z.infer<typeof luccaAbsenceHookSchema>;

// MagicWand
const magicWandSchema = buildLicenseHookSchema({
    label: z.string(),
    menuItems: z.array(z.object({ type: z.string(), text: z.string(), actions: z.string() })).nullable(),
});
export type MagicWandHook = z.infer<typeof magicWandSchema>;

// FAQ
const faqHookSchema = buildHookSchema({
    manage: z.boolean(),
});
export type FAQHook = z.infer<typeof faqHookSchema>;

// ContentFactory
const contentFactoryHookSchema = buildHookSchema({
    canCreateTemplate: z.boolean(),
    label: z.string(),
});
export type ContentFactoryHook = z.infer<typeof contentFactoryHookSchema>;

// AnimatedMeeting
const animatedMeetingHookSchema = buildHookSchema({
    label: z.string(),
    create: z.boolean(),
    description: z.string(),
    color: z.string(),
    icon: z.string(),
});
export type AnimatedMeetingHook = z.infer<typeof animatedMeetingHookSchema>;

// Jitsi
const jitsiHookSchema = buildHookSchema({
    label: z.string(),
    create: z.boolean(),
});
export type JitsiHook = z.infer<typeof jitsiHookSchema>;

// BigBlueButton
const bigBlueButtonHookSchema = buildLicenseHookSchema({
    label: z.string(),
});
export type BigBlueButtonHook = z.infer<typeof bigBlueButtonHookSchema>;

// VisioConnect
const visioConnectHookSchema = buildLicenseHookSchema({
    label: z.string(),
});
export type VisioConnectHook = z.infer<typeof visioConnectHookSchema>;

// Spot
const spotHookSchema = buildLicenseHookSchema({
    label: z.string(),
});

// BusinessContact

const businessContactHookSchema = buildHookSchema({
    label: z.string(),
});

/**
 * LicenseHookListType
 */
const licenseHookListTypeSchema = z
    .object({
        BigBlueButtonHook: bigBlueButtonHookSchema,
        ExternalAppHook: externalAppHookSchema,
        DigitalWorkplaceHook: digitalWorkplaceHookSchema,
        LuccaAbsenceHook: luccaAbsenceHookSchema,
        MagicPadHook: magicPadHookSchema,
        MagicWandHook: magicWandSchema,
        MediaLibraryHook: mediaLibraryHookSchema,
        OfficeHook: officeHookSchema,
        OnlyOfficeHook: onlyOfficeHookSchema,
        SpotHook: spotHookSchema,
        StudioHook: studioHookSchema,
        TwoFactorAuthenticationHook: twoFactorAuthenticationHookSchema,
        VisioConnectHook: visioConnectHookSchema,
    })
    .partial();
export type LicenseHookListType = z.infer<typeof licenseHookListTypeSchema>;

/**
 * HookListType
 */
export const hookListTypeSchema = z
    .object({
        AdminAdvancedHook: adminAdvancedHookSchema,
        AnimatedMeetingHook: animatedMeetingHookSchema,
        BusinessContactHook: businessContactHookSchema,
        calendarHook: calendarHookSchema,
        ContentFactoryHook: contentFactoryHookSchema,
        DriveHook: driveHookSchema,
        ExtraBotHook: extraBotHookSchema,
        FAQHook: faqHookSchema,
        FileBankHook: fileBankHookSchema,
        JitsiHook: jitsiHookSchema,
        MessengerHook: messengerHookSchema,
        SharedContentHook: sharedContentHookSchema,
        spotRssFeed: spotRssFeedSchema,
        WidgetHook: widgetHookSchema,
    })
    .partial()
    .and(licenseHookListTypeSchema);
export type HookListType = z.infer<typeof hookListTypeSchema>;
